import type { MicroServices } from "../interfaces/Config";
import { isProductionEnvironment } from "./env";

/**
 * Get allowed endpoint keys from MicroServices endpoints
 */
function getAllowedEndpointKeys(): (keyof MicroServices["endpoints"])[] {
  return [
    "auth",
    "users",
    "websockets",
    "resources",
    "nlp",
    "events",
    "pad",
    "insight",
    "agent",
  ];
}

/**
 * Check if overrides are allowed in the current environment
 */
export function isConfigOverrideAllowed(): boolean {
  return !isProductionEnvironment(window.location.hostname);
}

/**
 * Get configuration overrides from URL query parameters
 * Only works in non-production environments
 * Returns undefined when no overrides are present
 */
export function getConfigOverrides():
  | Partial<MicroServices["endpoints"]>
  | undefined {
  if (isProductionEnvironment(window.location.hostname)) {
    return undefined;
  }

  const overrides: Partial<MicroServices["endpoints"]> = {};
  const urlParams = new URLSearchParams(window.location.search);
  const allowedKeys = getAllowedEndpointKeys();

  allowedKeys.forEach((key) => {
    const paramValue = urlParams.get(key);
    if (paramValue) {
      overrides[key] = paramValue;
    }
  });

  return Object.keys(overrides).length > 0 ? overrides : undefined;
}

/**
 * Log active configuration overrides to console
 */
export function logConfigOverrides(
  overrides: Partial<MicroServices["endpoints"]>,
): void {
  console.warn("[Config Override Active]", overrides);
}

/**
 * Apply overrides to the resolved MicroServices config
 */
export function applyConfigOverrides(
  config: MicroServices,
  overrides: Partial<MicroServices["endpoints"]>,
): MicroServices {
  return {
    ...config,
    endpoints: {
      ...config.endpoints,
      ...overrides,
    },
  };
}
